import { Component, computed, EventEmitter, inject, Input, OnInit, Output } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import {
  CalculationModuleSignature,
  CMInput,
  CMLayerInput,
} from '@core/models/calculation-module.model'
import { CalculationModuleService } from '@core/services/calculation-module.service'
import { isNullOrUndefinedString } from '@core/services/core.utilities'
import { SelectionScaleService } from '@core/services/selection-scale.service'
import { SelectionToolService } from '@core/services/selection-tool.service'
import { ToasterService } from '@core/services/toaster.service'
import { MatomoTracker } from 'ngx-matomo-client'
import { CmInputComponent } from './cm-input/cm-input.component'
import { DynamicCmInputArrayComponent } from './dynamic-cm-inputs-array/dynamic-cm-inputs-array.component'
import { DynamicCmInputsOptionsComponent } from './dynamic-cm-inputs-options/dynamic-cm-inputs-options.component'
import { ImportCmInputsComponent } from './import-cm-inputs/import-cm-inputs.component'
@Component({
  selector: 'app-cm-selected',
  templateUrl: './cm-selected.component.html',
  styleUrl: './cm-selected.component.scss',
  imports: [
    // Components
    ImportCmInputsComponent,
    CmInputComponent,
    DynamicCmInputArrayComponent,
    DynamicCmInputsOptionsComponent,

    // Forms
    FormsModule,
    ReactiveFormsModule,

    // Material
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
  ],
})
export class CmSelectedComponent implements OnInit {
  @Input({ required: true }) cmSelected!: CalculationModuleSignature
  @Output() unselectCMEvent = new EventEmitter<void>()
  readonly calculationModuleService = inject(CalculationModuleService)
  private readonly _toasterService = inject(ToasterService)
  private readonly _selectionToolService = inject(SelectionToolService)
  private readonly _selectionScaleService = inject(SelectionScaleService)
  private readonly _tracker = inject(MatomoTracker)

  RABBIT_MAX_MESSAGE_SIZE = 25000000

  isCMRunning$ = this.calculationModuleService.isCMRunning$

  cmInputsState$ = this.calculationModuleService.cmInputsState$
  cmInputs$ = this.calculationModuleService.cmInputs$
  inputsByCategories = computed(() => [
    { id: 0, name: 'Inputs', inputs: this.cmInputs$().filter((x) => x.input_priority === 0) },
    { id: 1, name: 'Basic inputs', inputs: this.cmInputs$().filter((x) => x.input_priority === 1) },
    {
      id: 2,
      name: 'Advanced inputs: (Level 1)',
      inputs: this.cmInputs$().filter((x) => x.input_priority === 2),
    },
    {
      id: 3,
      name: 'Advanced inputs: (Level 2)',
      inputs: this.cmInputs$().filter((x) => x.input_priority === 3),
    },
    {
      id: 4,
      name: 'Advanced inputs: (Level 3)',
      inputs: this.cmInputs$().filter((x) => x.input_priority === 4),
    },
    {
      id: 5,
      name: 'Layer inputs',
      inputs: this.cmInputs$().filter((x) => x.input_priority === 5),
    },
  ])

  // Prefix session
  prefixSessionName = ''
  today = new Date()
  defaultPrefix =
    this.today.toLocaleDateString('en-CH') +
    ' ' +
    String(this.today.getHours()).padStart(2, '0') +
    ':' +
    String(this.today.getMinutes()).padStart(2, '0')

  ngOnInit() {
    this.calculationModuleService.initCMInputs(this.cmSelected.cm_id)
  }

  toggleCMPanel() {
    // Add logic to unselect current CM in parent
    this.unselectCMEvent.emit()
  }

  handleImportedData(data: any) {
    try {
      this._updateFields(data)
    } catch {
      this._toasterService.openSnackBarError('Error importing inputs')
    }
  }

  formatLevelAvailabilityToolTip(cm: CalculationModuleSignature) {
    if (!cm.authorized_scale) {
      return `No authorized scale for this CM.`
    }
    const formattedLevels =
      cm.authorized_scale.length > 1
        ? cm.authorized_scale.slice(0, -1).join(', ') +
          ' and ' +
          cm.authorized_scale[cm.authorized_scale.length - 1]
        : cm.authorized_scale[0]

    if (!this._validateAuthorizedScale(cm)) {
      return `This CM is only available at ${formattedLevels} levels.`
    }

    if (!this._selectionToolService.selectedSurface$()) {
      return `Please select a zone to run this cm.`
    }
    if (this._selectionToolService.isCMSelectionSurfaceTooSmall()) {
      return `The selected zones are too small.`
    }

    if (!this._selectionToolService.isCMSelectionSurfaceCorrect()) {
      return `The selected zones are too big or too far apart.`
    }

    return `CM available with selection at ${formattedLevels} levels`
  }

  isRunCMDisabled(cm: CalculationModuleSignature) {
    return (
      !this._isCmAlwaysAvailable(cm) &&
      (this._selectionToolService.selectedSurface$() <= 0 ||
        this._selectionToolService.isCMSelectionSurfaceTooSmall() ||
        !this._validateAuthorizedScale(cm) ||
        !this._selectionToolService.isCMSelectionSurfaceCorrect())
    )
  }

  runCM() {
    if (this.prefixSessionName == '') {
      // default value is date time at local format
      this.prefixSessionName = this.defaultPrefix
    }

    this.cmSelected['cm_prefix'] = this.prefixSessionName

    //@todo, remove old inputs cm payload logic
    this.calculationModuleService.setCurrentCMPayload(this.cmSelected)
    const payload = this.calculationModuleService.currentCMPayload$()

    if (
      payload &&
      this.calculationModuleService.getPayloadSizeInBytes(payload) >= this.RABBIT_MAX_MESSAGE_SIZE
    ) {
      this._toasterService.openSnackBarError(
        'Payload is too large, use smaller or less upload files for CM run',
      )
      return
    }
    this.calculationModuleService.runCM()
    // Matomo
    this._tracker.trackEvent('CM', 'Calculation Module ran', this.cmSelected.cm_name)

    // To prevent running CM while having draw tool selected
    this._selectionToolService.selectDrawTool('click')
  }

  stopCM() {
    this.calculationModuleService.cancelRunCM()
  }

  //@todo, re-implement with dynamic inputs
  private _updateFields(uploadedData: { [key: string]: string }[]): void {
    if (!uploadedData || !this.cmSelected) return

    // Check for dynamic inputs
    const containsNonCMInput = this.cmInputs$().some(
      (input) =>
        this.calculationModuleService.isDynamicCMInputArray(input) ||
        this.calculationModuleService.isDynamicCMInputOptions(input),
    )

    if (containsNonCMInput) {
      console.warn('Aborting _updateFields: contains dynamic inputs')
      this._toasterService.openSnackBarError(
        'Error importing inputs: import not available for dynamic inputs',
      )
      return
    }

    // Explicitly narrow inputs to CMInput[]
    const cmInputs = this.cmInputs$() as (CMInput | CMLayerInput)[]

    // Get key dynamically
    const key = uploadedData.find((line) => {
      if (line['1']) {
        return line
      }
    })
    if (!key) {
      return
    }
    const key1 = key['1'].replace(/"/g, '')
    const key2 = key['2'].replace(/"/g, '')

    // Update regular inputs
    let inputsErrorCounter = 0
    let inputCounter = 0
    cmInputs.forEach((component) => {
      if (this.calculationModuleService.isCMLayerInput(component)) return
      inputCounter++
      const indicator = component.input_name

      const correspondingField = uploadedData.find(
        (line) => line[key1] && line[key1].trim() === indicator.trim(),
      )

      if (correspondingField && correspondingField.hasOwnProperty('value')) {
        let rawValue: string = correspondingField[key2]

        if (component.input_type === 'checkbox') {
          rawValue = rawValue
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v.length > 0)
            .join(',')
        }
        this.calculationModuleService.updateInputValue(component, rawValue)
      } else {
        inputsErrorCounter++
        console.warn('Input not found in imported data:', indicator)
      }
    })

    // If not 1 input found a match, stops the import
    if (inputsErrorCounter != 0 && inputsErrorCounter == inputCounter) {
      this._toasterService.openSnackBarError(
        'Unable to import the inputs, make sure to select the csv file with the parameters of the active CM.',
      )
      return
    }

    // Update layers
    let layersErrorCounter = 0
    let layerCounter = 0
    this.cmInputs$().forEach((layerInput) => {
      if (!this.calculationModuleService.isCMLayerInput(layerInput)) return

      layerCounter++
      const fieldName = layerInput.input_name

      let importedLayer = uploadedData.find(
        (line) => line[key1] && line[key1].trim() === fieldName?.trim(),
      )
      if (!importedLayer) {
        importedLayer = uploadedData.find(
          (line) =>
            line[key2] && layerInput.layers.some((l) => l.name.trim() === line[key2].trim()),
        )
      }

      if (importedLayer && importedLayer[key2]) {
        const targetLayerName = importedLayer[key2].trim()
        const matchingLayer = layerInput.layers.find(
          (layer) => layer.name.trim() === targetLayerName,
        )

        if (matchingLayer) {
          // Create synthetic event object expected by setLayerNeeded @todo, maybe better to have setLayerNeeded expect just the name

          const syntheticEvent = {
            target: {
              value: matchingLayer.name,
            },
          }
          this.calculationModuleService.setLayerNeeded(layerInput, syntheticEvent)
        } else {
          layersErrorCounter++
          console.warn('Layer not found:', targetLayerName)
        }
      } else {
        layersErrorCounter++
        console.warn('Layer input not found in imported data:', fieldName)
      }
    })

    // If not 1 input found a match, stops the import
    if (layersErrorCounter != 0 && layersErrorCounter == layerCounter) {
      this._toasterService.openSnackBarError(
        'Unable to import the inputs, make sure to select the csv file with the parameters of the active CM.',
      )
      return
    } else if (layersErrorCounter > 0 || inputsErrorCounter > 0) {
      this._toasterService.openSnackBarError(
        'Error during import: at least 1 input cannot be imported',
      )
      return
    }

    if (layersErrorCounter > 0 || inputsErrorCounter > 0) {
      this._toasterService.openSnackBarError('Warning: Some inputs or layers could not be imported')
      return
    }

    this._toasterService.openSnackBar('Inputs imported successfully')
  }

  private _isCmAlwaysAvailable(cm: CalculationModuleSignature) {
    return isNullOrUndefinedString(cm.authorized_scale) || cm.authorized_scale?.length === 0
  }

  private _validateAuthorizedScale(cm: CalculationModuleSignature) {
    const displayName = this._selectionScaleService.currentScaleLevel$()?.displayName
    if (
      !isNullOrUndefinedString(cm.authorized_scale) &&
      cm.authorized_scale &&
      cm.authorized_scale.length >= 1 &&
      displayName
    ) {
      return cm.authorized_scale?.includes(displayName)
    }
    return true
  }
}
